/**
 * F14.1 — I18nProvider expõe `locale` + `setLocale` + função `t(key)`
 * via Context. Source of truth do locale inicial:
 *
 *   1. localStorage `edsp.locale` (se válido).
 *   2. `app_users.locale` do backend (sync após login — F14.4).
 *   3. `DEFAULT_LOCALE` (`fr`).
 *
 * Persistência: sempre que `setLocale` é chamado, atualiza
 * localStorage imediatamente (pre-auth). O sync com backend
 * (`users.updateLocale`) é responsabilidade de quem chama setLocale —
 * mantido fora do Provider para evitar acoplamento com tRPC.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { DEFAULT_LOCALE, LOCALES, MESSAGES, type AllMessages, type Locale } from "./messages.js";

const STORAGE_KEY = "edsp.locale";

interface I18nContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: TFunction;
}

const I18nContext = createContext<I18nContextValue | null>(null);

/* ─── useT() — assinatura type-safe via path string ──────────────── */

/**
 * Type-safe key path: `"auth.emailLabel"` |
 *                     `"common.loading"` | etc.
 *
 * Apenas 2 níveis de aninhamento (suficiente pro escopo atual).
 */
type Paths<T, Prefix extends string = ""> = {
  [K in keyof T & string]: T[K] extends Record<string, unknown>
    ? Paths<T[K], `${Prefix}${K}.`>
    : `${Prefix}${K}`;
}[keyof T & string];

export type MessageKey = Paths<AllMessages>;
type TFunction = (key: MessageKey) => string;

/**
 * F-landing-i18n — detecta o idioma a partir do navegador (navigator.language /
 * languages). Mapeia só o prefixo (`pt-BR` → `pt`, `fr-FR` → `fr`, `en-US` →
 * `en`). É a "localização" do usuário sem pedir permissão de GPS/IP.
 */
function detectBrowserLocale(): Locale | null {
  if (typeof navigator === "undefined") return null;
  const langs =
    navigator.languages && navigator.languages.length > 0
      ? navigator.languages
      : [navigator.language];
  for (const lang of langs) {
    const prefix = (lang || "").toLowerCase().split("-")[0] ?? "";
    if ((LOCALES as readonly string[]).includes(prefix)) return prefix as Locale;
  }
  return null;
}

function loadInitialLocale(): Locale {
  if (typeof window === "undefined") return DEFAULT_LOCALE;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw && (LOCALES as readonly string[]).includes(raw)) {
      // Preferência salva (usuário escolheu no switcher) tem prioridade.
      return raw as Locale;
    }
  } catch {
    // localStorage indisponível (modo privado, quota) — segue para detecção.
  }
  // Sem preferência salva → idioma do navegador; senão o default (fr).
  return detectBrowserLocale() ?? DEFAULT_LOCALE;
}

function resolveKey(messages: AllMessages, key: string): string {
  const parts = key.split(".");
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let cursor: any = messages;
  for (const part of parts) {
    cursor = cursor?.[part];
    if (cursor === undefined) return key; // fallback visível: mostra a key bruta
  }
  return typeof cursor === "string" ? cursor : key;
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(loadInitialLocale);

  // F-locale-detect (CEO 2026-09-20) — só o `lang` do documento acompanha o
  // estado. A GRAVAÇÃO mudou de lugar de propósito: antes este efeito rodava
  // na montagem e escrevia o idioma DETECTADO no localStorage, ou seja, o
  // palpite virava "preferência do usuário" na primeira visita e a detecção
  // nunca mais rodava — nem se a pessoa trocasse o idioma do navegador.
  // Agora só a escolha EXPLÍCITA (switcher ou idioma da conta) é gravada.
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = locale;
    }
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    if (!(LOCALES as readonly string[]).includes(next)) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // localStorage indisponível (modo privado, quota) — segue só em memória.
    }
    setLocaleState(next);
  }, []);

  const t = useCallback<TFunction>((key) => resolveKey(MESSAGES[locale], key), [locale]);

  const value = useMemo<I18nContextValue>(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error(
      "useI18n() used outside <I18nProvider>. Wrap your app root with <I18nProvider>.",
    );
  }
  return ctx;
}

/** Shortcut quando você só precisa de `t`. */
export function useT(): TFunction {
  return useI18n().t;
}
